import { chatLogPrefix, getChatDomIds } from "./namespace";

export type WidgetKind = "floating" | "embed";

interface MountSlot {
  kind: WidgetKind;
  siteId: string;
  containerSelector: string | null;
  /** False until `createWidget` attaches a shadow root. In-flight slots stay reserved. */
  mounted: boolean;
}

const slots: MountSlot[] = [];

/** Preferred embed host for dual-mount (`#${ns}-chat-embed`). */
export function defaultEmbedSelector(): string {
  return `#${getChatDomIds().embed}`;
}

export function floatingHostSelector(): string {
  return `#${getChatDomIds().host}`;
}

/**
 * Embed container: explicit `container`, else `#${ns}-chat-embed` if present,
 * else `#${ns}-chat-widget` when that node exists and is not already a widget
 * (legacy embed-only snippets).
 */
export function resolveEmbedSelector(container?: string | null): string {
  if (container) {
    return container;
  }

  const ids = getChatDomIds();
  if (document.getElementById(ids.embed)) {
    return `#${ids.embed}`;
  }

  const legacy = document.getElementById(ids.host);
  if (legacy && !legacy.shadowRoot) {
    return `#${ids.host}`;
  }

  return defaultEmbedSelector();
}

function pruneDeadSlots(): void {
  for (let i = slots.length - 1; i >= 0; i -= 1) {
    if (!isSlotLive(slots[i])) {
      slots.splice(i, 1);
    }
  }
}

function hostForSlot(slot: MountSlot): HTMLElement | null {
  if (slot.kind === "floating") {
    return document.getElementById(getChatDomIds().host);
  }
  const el = document.querySelector(
    slot.containerSelector || defaultEmbedSelector(),
  );
  return el instanceof HTMLElement ? el : null;
}

function isSlotLive(slot: MountSlot): boolean {
  if (!slot.mounted) {
    return true;
  }
  const el = hostForSlot(slot);
  return Boolean(el?.isConnected && el.shadowRoot);
}

function embedTargetsFloatingHost(selector: string): boolean {
  const floating = document.getElementById(getChatDomIds().host);
  const embed = document.querySelector(selector);
  if (floating && embed && floating === embed) {
    return true;
  }
  return selector === floatingHostSelector();
}

export function hasFloatingMount(): boolean {
  pruneDeadSlots();
  return slots.some((slot) => slot.kind === "floating");
}

export function hasEmbedMount(): boolean {
  pruneDeadSlots();
  return slots.some((slot) => slot.kind === "embed");
}

export function getMountedSiteId(): string | undefined {
  pruneDeadSlots();
  return slots[0]?.siteId;
}

export function mountCount(): number {
  pruneDeadSlots();
  return slots.length;
}

/**
 * Synchronously reserve a floating or embed slot before `initialize` / render.
 *
 * A slot whose host was removed from the DOM (SPA unmount) is dropped so
 * `load()` can run again. In-flight reservations are kept until render or
 * `releaseMount`.
 *
 * Allowed: one floating, one embed, same site slug. Form-only + chat dual
 * mount is rejected later once the load payload is known.
 */
export function tryReserveMount(
  kind: WidgetKind,
  siteId: string,
  containerSelector: string | null,
): { ok: true } | { ok: false; message: string } {
  pruneDeadSlots();

  const existingSiteId = slots[0]?.siteId;
  if (existingSiteId && existingSiteId !== siteId) {
    return {
      ok: false,
      message: `${chatLogPrefix()} Chat widget is already loaded for "${existingSiteId}".`,
    };
  }

  if (kind === "floating" && slots.some((slot) => slot.kind === "floating")) {
    return {
      ok: false,
      message: `${chatLogPrefix()} Floating chat widget is already on this page.`,
    };
  }

  if (kind === "embed" && slots.some((slot) => slot.kind === "embed")) {
    return {
      ok: false,
      message: `${chatLogPrefix()} Embedded chat widget is already on this page.`,
    };
  }

  const embedSelector =
    kind === "embed" ? resolveEmbedSelector(containerSelector) : null;

  if (
    kind === "embed" &&
    embedSelector &&
    slots.some((slot) => slot.kind === "floating") &&
    embedTargetsFloatingHost(embedSelector)
  ) {
    return {
      ok: false,
      message: `${chatLogPrefix()} Embed cannot use ${floatingHostSelector()} while the floating widget is on the page. Use ${defaultEmbedSelector()} or pass container.`,
    };
  }

  if (kind === "floating") {
    const embedSlot = slots.find((slot) => slot.kind === "embed");
    if (
      embedSlot?.containerSelector &&
      embedTargetsFloatingHost(embedSlot.containerSelector)
    ) {
      return {
        ok: false,
        message: `${chatLogPrefix()} Floating widget cannot use ${floatingHostSelector()} because embed already mounted there. Use ${defaultEmbedSelector()} for embed.`,
      };
    }
  }

  slots.push({
    kind,
    siteId,
    containerSelector: embedSelector,
    mounted: false,
  });
  return { ok: true };
}

export function confirmMount(kind: WidgetKind): void {
  const slot = slots.find((candidate) => candidate.kind === kind);
  if (slot) {
    slot.mounted = true;
  }
}

export function releaseMount(kind: WidgetKind): void {
  const index = slots.findIndex((slot) => slot.kind === kind);
  if (index >= 0) {
    slots.splice(index, 1);
  }
}
